/** 対応するファイルタイプ */
export const FILE_TYPES = {
  pdf: 'pdf',
  image: 'image',
  excel: 'excel',
  docx: 'docx',
  doc: 'doc',
  csv: 'csv',
  unknown: 'unknown',
} as const

export type FileType = typeof FILE_TYPES[keyof typeof FILE_TYPES]

/** 対応するファイル拡張子 */
type SupportedExtension =
  | '.pdf'
  | '.png'
  | '.jpg'
  | '.jpeg'
  | '.gif'
  | '.webp'
  | '.xlsx'
  | '.xls'
  | '.docx'
  | '.doc'
  | '.csv'

/** 拡張子とファイルタイプのマッピング */
interface ExtensionMapping {
  readonly ext: SupportedExtension
  readonly type: FileType
}

/** 拡張子とファイルタイプのマッピング */
const EXTENSION_MAPPINGS: readonly ExtensionMapping[] = [
  { ext: '.pdf', type: FILE_TYPES.pdf },
  { ext: '.png', type: FILE_TYPES.image },
  { ext: '.jpg', type: FILE_TYPES.image },
  { ext: '.jpeg', type: FILE_TYPES.image },
  { ext: '.gif', type: FILE_TYPES.image },
  { ext: '.webp', type: FILE_TYPES.image },
  { ext: '.xlsx', type: FILE_TYPES.excel },
  { ext: '.xls', type: FILE_TYPES.excel },
  { ext: '.docx', type: FILE_TYPES.docx },
  { ext: '.doc', type: FILE_TYPES.doc },
  { ext: '.csv', type: FILE_TYPES.csv },
]

/**
 * ファイル拡張子からファイルタイプを判定する
 *
 * @param filename - ファイル名
 * @returns 判定されたファイルタイプ
 */
export function getFileTypeFromName(filename: string): FileType {
  const lowerName = filename.toLowerCase()

  const matched = EXTENSION_MAPPINGS.find((mapping) => lowerName.endsWith(mapping.ext))
  return matched?.type ?? FILE_TYPES.unknown
}

/**
 * ファイルタイプが画像かどうかを判定する（型ガード）
 *
 * @param fileType - ファイルタイプ
 * @returns 画像タイプの場合true
 */
export function isImageType(fileType: FileType): fileType is typeof FILE_TYPES.image {
  return fileType === FILE_TYPES.image
}

/**
 * ファイルタイプがExcelかどうかを判定する（型ガード）
 *
 * @param fileType - ファイルタイプ
 * @returns Excelタイプの場合true
 */
export function isExcelType(fileType: FileType): fileType is typeof FILE_TYPES.excel {
  return fileType === FILE_TYPES.excel
}

/**
 * ファイルタイプがDOCXかどうかを判定する（型ガード）
 *
 * @param fileType - ファイルタイプ
 * @returns DOCXタイプの場合true
 */
export function isDocxType(fileType: FileType): fileType is typeof FILE_TYPES.docx {
  return fileType === FILE_TYPES.docx
}

/**
 * ファイルタイプがDOC（レガシー形式）かどうかを判定する（型ガード）
 *
 * @param fileType - ファイルタイプ
 * @returns DOCタイプの場合true
 */
export function isDocType(fileType: FileType): fileType is typeof FILE_TYPES.doc {
  return fileType === FILE_TYPES.doc
}
